import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import ImageUpload from "./ImageUpload";
import "./Home.css"; // Use home page styling
import "./ProductEdit.css";

export default function ProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    image: "",
    availability: "In stock"
  });

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      // If no id, we're adding a new product
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const productRef = doc(db, "products", id);
        const productSnap = await getDoc(productRef);
        
        if (productSnap.exists()) {
          const data = productSnap.data();
          setFormData({
            name: data.name || "",
            price: data.price || "",
            image: data.image || "",
            availability: data.availability || "In stock"
          });
        } else {
          alert("Product not found!");
          navigate("/admin");
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        alert("Error loading product data");
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, navigate]);

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Update or add product
  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.price || !formData.image) {
      alert("Please fill in all required fields and ensure an image is uploaded");
      return;
    }
    
    try {
      // Check authentication
      if (!auth.currentUser) {
        alert("You must be logged in to manage products. Please log in first.");
        return;
      }
      
      if (id) {
        // Edit existing product
        const updateData = {
          name: formData.name,
          price: formData.price,
          image: formData.image,
          availability: formData.availability,
          updatedAt: serverTimestamp()
        };
        
        const productRef = doc(db, "products", id);
        await updateDoc(productRef, updateData);
        
        alert("Product updated successfully!");
      } else {
        // Add new product
        const newProductData = {
          name: formData.name,
          price: formData.price,
          image: formData.image,
          availability: formData.availability,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        await addDoc(collection(db, "products"), newProductData);
        
        alert("Product added successfully!");
      }
      
      navigate("/admin");
    } catch (error) {
      console.error(id ? "Error updating product:" : "Error adding product:", error);
      if (error.code === 'permission-denied') {
        alert("Permission denied. Make sure you're logged in with the admin email.");
      } else {
        alert(`Error ${id ? 'updating' : 'adding'} product: ${error.message}`);
      }
    }
  };

  // Cancel and go back
  const handleCancel = () => {
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="home-container">
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          <p>Loading product data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      {/* Header - matching home page style */}
      <div className="latest-header-white">
        <button onClick={handleCancel} className="see-all-btn">
          ← Back to Admin
        </button>
        <h3 className="latest-title">{id ? 'Edit Product' : 'Add New Product'}</h3>
      </div>

      {/* Edit Form - in grey section like home page */}
      <div className="latest-section">
        <form onSubmit={handleUpdateProduct} className="edit-form">
          <div className="form-group">
            <label className="form-label">Product Name:</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder="Enter product name"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Price:</label>
            <input
              type="text"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder="e.g., 45000 Ks"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Stock Status:</label>
            <select
              name="availability"
              value={formData.availability}
              onChange={handleInputChange}
              className="form-input"
            >
              <option value="In stock">In Stock</option>
              <option value="Sold out">Sold Out</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Product Image:</label>
            <ImageUpload 
              onImageUploaded={(imageUrl) => setFormData({...formData, image: imageUrl})}
              currentImage={formData.image}
            />
            {formData.image && (
              <div className="current-image-preview">
                <img src={formData.image} alt="Current product" className="preview-img" />
                <p className="preview-label">Current Image</p>
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <button type="submit" className="btn-update">
              {id ? 'Update Product' : 'Add Product'}
            </button>
            <button type="button" onClick={handleCancel} className="btn-cancel">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
